"use client";

import { motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import {
  ClockIcon,
  SearchIcon,
  ShieldCheckIcon,
  UsersIcon,
} from "lucide-react";

import { Container } from "@/app/(marketing)/_components/ui/container";

interface Feature {
  icon: LucideIcon;
  title: string;
  description: string;
  highlights: string[];
}

const features: Feature[] = [
  {
    icon: ShieldCheckIcon,
    title: "Multi-Layer Verification",
    description:
      "ID verification plus education and professional background checks weed out experts who aren't fit for qualitative research.",
    highlights: [
      "Three-way location match (ID, IP, social)",
      "Device tracking to prevent fraud",
      "Unusual behavior and AI-response detection",
    ],
  },
  {
    icon: SearchIcon,
    title: "AI-Led Screening",
    description:
      "Conversational screening with visual and audio checks eliminates common fraud and bot issues that harm research integrity.",
    highlights: [
      "Voice and video onboarding",
      "Per-interview screening with our AI agent",
      "Quality fit assessment for your needs",
    ],
  },
  {
    icon: UsersIcon,
    title: "Continuous Improvement",
    description:
      "Submit feedback per expert, and Terac fine-tunes your custom screening agent on that feedback.",
    highlights: [
      "Per-expert feedback collection",
      "Custom screening agent tuning",
      "Consistent quality over time",
    ],
  },
  {
    icon: ClockIcon,
    title: "Camera-Ready Experts",
    description:
      "Start receiving qualified, verified experts the same day you launch your study.",
    highlights: [
      "24/7 AI recruitment agents",
      "Real-time qualification status",
      "Context you need before interviews",
    ],
  },
];

export default function UserResearchFeatures() {
  return (
    <section className="py-20">
      <Container>
        {/* Section header */}
        <div className="mb-16 flex flex-col items-center gap-8">
          <div className="bg-marketing-card-blue rounded-t-[10px] px-2.5 py-1.5">
            <p className="text-marketing-blue font-mono text-sm tracking-[-0.28px]">
              Capabilities
            </p>
          </div>
          <h2 className="text-marketing-text dark:text-marketing-cream max-w-[700px] text-center text-3xl leading-[1.1] font-medium tracking-[-1.5px] md:text-4xl lg:text-[50px]">
            Quality recruitment that moves at your speed
          </h2>
        </div>

        {/* Feature cards */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, transform: "translateY(20px)" }}
              whileInView={{ opacity: 1, transform: "translateY(0px)" }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="border-marketing-border bg-marketing-cream-light rounded-[20px] border p-8"
            >
              <div className="bg-marketing-card-blue mb-4 flex h-12 w-12 items-center justify-center rounded-full">
                <feature.icon className="text-marketing-blue h-6 w-6" />
              </div>
              <h3 className="text-marketing-text mb-3 text-xl font-medium">
                {feature.title}
              </h3>
              <p className="text-marketing-text-muted mb-6 text-base leading-relaxed">
                {feature.description}
              </p>
              <div className="flex flex-col gap-2">
                {feature.highlights.map((highlight) => (
                  <div key={highlight} className="flex items-center gap-2.5">
                    <div className="bg-marketing-blue h-1.5 w-1.5 shrink-0 rounded-full" />
                    <p className="text-marketing-text text-sm font-medium">
                      {highlight}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </Container>
    </section>
  );
}
